import React from 'react';
import { View, Pressable } from 'react-native';
import { useRouter, usePathname } from 'expo-router';
import { useLanding } from '@/components/Landing/LandingKit';
import { DocsTopicLink } from './DocsDesign';
import { useTheme } from '@/context/ThemeContext';
import { isDarkTheme } from '@/constants/Colors';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import useResponsiveness from '@/hooks/useResponsiveness';

// Renders a "What you'll learn" / overview card at the top of a docs page,
// between the DocsHeader and the first DocsSection.

export const DocsPageOverview = ({
  items = [],
  readTime,
  prerequisites = [],
}) => {
  const { colors, theme } = useTheme();
  const { isXS } = useResponsiveness();
  const router = useRouter();
  const isDark = isDarkTheme(theme);
  const isDocs = /^\/(docs|learn)(\/|$)/.test(usePathname());
  const { c } = useLanding();

  if (items.length === 0) return null;
  if (isDocs) return (
    <View style={{ borderTopWidth: 1, borderBottomWidth: 1, borderColor: c.rule, paddingVertical: 22, marginBottom: 36 }}>
      <View style={{ flexDirection: 'row', alignItems: 'baseline', justifyContent: 'space-between', gap: 16, marginBottom: 16 }}>
        <Text weight="semibold" style={{ color: c.ink, fontSize: 15, lineHeight: 23 }}>In this guide</Text>
        {readTime ? <Text style={{ color: c.muted, fontSize: 12 }}>{readTime} min read</Text> : null}
      </View>
      {items.map((item, index) => <View key={index} style={{ flexDirection: 'row', gap: 12, marginBottom: 8 }}><Text style={{ color: c.muted, fontSize: 15, lineHeight: 24 }}>·</Text><Text style={{ flex: 1, color: c.inkSoft, fontSize: 15, lineHeight: 24 }}>{item}</Text></View>)}
      {prerequisites.length ? <View style={{ marginTop: 16 }}><Text style={{ color: c.muted, fontSize: 13 }}>Before you start</Text>{prerequisites.map((item, index) => item.href ? <DocsTopicLink key={index} compact href={item.href} title={item.label} /> : <Text key={index} style={{ color: c.inkSoft, fontSize: 14, lineHeight: 24 }}>{item.label}</Text>)}</View> : null}
    </View>
  );

  return (
    <View
      style={{
        padding: isXS ? 20 : 24,
        borderRadius: 16,
        backgroundColor: isDark
          ? 'rgba(59,130,246,0.06)'
          : 'rgba(59,130,246,0.04)',
        borderWidth: 1,
        borderColor: isDark
          ? 'rgba(59,130,246,0.15)'
          : 'rgba(59,130,246,0.1)',
        marginBottom: isXS ? 32 : 40,
      }}
    >
      {/* Header row */}
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 16,
        }}
      >
        <View
          style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}
        >
          <View
            style={{
              width: 28,
              height: 28,
              borderRadius: 8,
              backgroundColor: 'rgba(59,130,246,0.15)',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icon name="list-check" size={14} color="#3B82F6" solid />
          </View>
          <Text tag="h2" size="l" weight="semibold">
            What you'll learn
          </Text>
        </View>
        {readTime && (
          <View
            style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}
          >
            <Icon name="clock" size={12} color={colors.text_color_tertiary} />
            <Text tag="span" size="n" color="tertiary">
              {readTime} min read
            </Text>
          </View>
        )}
      </View>

      {/* Items */}
      {items.map((item, index) => (
        <View
          key={index}
          style={{
            flexDirection: 'row',
            alignItems: 'flex-start',
            gap: 10,
            marginBottom: index < items.length - 1 ? 10 : 0,
          }}
        >
          <Icon
            name="circle-check"
            size={16}
            color="#3B82F6"
            solid
            style={{ marginTop: 2 }}
          />
          <Text
            tag="p"
            size="m"
            color="secondary"
            style={{ flex: 1, lineHeight: 24 }}
          >
            {item}
          </Text>
        </View>
      ))}

      {/* Prerequisites */}
      {prerequisites.length > 0 && (
        <View
          style={{
            marginTop: 16,
            paddingTop: 16,
            borderTopWidth: 1,
            borderTopColor: isDark
              ? 'rgba(59,130,246,0.12)'
              : 'rgba(59,130,246,0.08)',
          }}
        >
          <Text
            tag="h3"
            size="n"
            weight="medium"
            color="secondary"
            style={{ marginBottom: 8 }}
          >
            Prerequisites
          </Text>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
            {prerequisites.map((prereq, index) => (
              <Pressable
                key={index}
                onPress={() => prereq.href && router.push(prereq.href)}
                style={({ hovered }) => ({
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 6,
                  paddingHorizontal: 12,
                  paddingVertical: 6,
                  borderRadius: 100,
                  backgroundColor: hovered
                    ? isDark
                      ? 'rgba(59,130,246,0.15)'
                      : 'rgba(59,130,246,0.1)'
                    : isDark
                      ? 'rgba(59,130,246,0.08)'
                      : 'rgba(59,130,246,0.05)',
                  transition: 'all 0.15s ease',
                })}
              >
                <Icon
                  name="arrow-right"
                  size={10}
                  color="#3B82F6"
                />
                <Text tag="span" size="n" style={{ color: '#3B82F6' }}>
                  {prereq.label}
                </Text>
              </Pressable>
            ))}
          </View>
        </View>
      )}
    </View>
  );
};

export default DocsPageOverview;
